<template>
  <v-row>
    <v-col cols="6" class="py-0">
      <v-card class="elevation-0 card-border" height="190">
        <v-card-title>
          <v-row>
            <v-col cols="12" class="py-0">
              <h3 class="mb-0 input-text_title">{{ quickActionsData[detailIndicator].cardOne.title }}</h3>
            </v-col>
            <v-col cols="12" class="py-0">
              <p class="mb-0 input-text_subtitle light-grey">
                {{ quickActionsData[detailIndicator].cardOne.description }}
              </p>
            </v-col>
          </v-row>
        </v-card-title>
        <v-card-text style="height: 90px">
          <v-row align="center">
            <v-col v-if="loading" cols="12">
              <v-skeleton-loader class="mt-n6" type="card-heading" />
            </v-col>
            <v-col v-else class="d-flex" cols="12">
              <h1 class="mb-0 percentual-text text-wrap">{{ quickActionsData[detailIndicator].cardOne.value | localeString(0) }}</h1>
              <mf-quick-actions
                class="pl-2"
                :quick-action-group-id="quickActionsData[detailIndicator].cardOne.quickActionGroupId"
                :short-description="quickActionsData[detailIndicator].cardOne.title"
                :long-description="quickActionsData[detailIndicator].cardOne.title"
                :segmentation-group="quickActionsData[detailIndicator].cardOne.segmentationGroup"
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-col>
    <v-col v-if="quickActionsData[detailIndicator].cardTwo" cols="6" class="py-0">
      <v-card class="elevation-0 card-border" height="190">
        <v-card-title class="d-flex">
          <v-row>
            <v-col cols="12" class="py-0">
              <h3 class="mb-0 input-text_title">{{ quickActionsData[detailIndicator].cardTwo.title }}</h3>
            </v-col>
            <v-col cols="12" class="py-0">
              <p class="mb-0 input-text_subtitle light-grey">
                {{ quickActionsData[detailIndicator].cardTwo.description }}
              </p>
            </v-col>
          </v-row>
        </v-card-title>
        <v-card-text style="height: 90px">
          <v-row align="center">
            <v-col v-if="loading" cols="12">
              <v-skeleton-loader class="mt-n6" type="card-heading" />
            </v-col>
            <v-col v-else class="d-flex" cols="12">
              <h1 class="mb-0 percentual-text">{{ quickActionsData[detailIndicator].cardTwo.value | localeString(0) }}</h1>
              <mf-quick-actions
                ref="quickAction"
                class="pl-2"
                :quick-action-group-id="quickActionsData[detailIndicator].cardTwo.quickActionGroupId"
                :short-description="quickActionsData[detailIndicator].cardTwo.title"
                :long-description="quickActionsData[detailIndicator].cardTwo.title"
                :segmentation-group="quickActionsData[detailIndicator].cardTwo.segmentationGroup"
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { QUERY_GET_DETAILS } from '../../graphql'
import { DateTime } from 'luxon'
import {
  criteriaGroup,
  totalClientsBelowAverageProductValue,
  totalClientsDecreasedAverageItemsInBasket,
  customerNeverBought,
  customerDidNotBoughtInPeriod,
  totalClientsDecreasedAverageProductValue,
  totalClientsBelowAverageCoupons,
  isInSegmentCriteria,
} from '@/components/quick-actions/segmentationGroups.js'

export default {
  name: 'DecompositionDetailsQuickActions',
  props: {
    newSearch: {
      type: Boolean,
      required: true,
    },
    averageItems: {
      type: Number,
      required: true,
    },
    averageCoupons: {
      type: Number,
      required: true,
    },
    averageProducts: {
      type: Number,
      required: true,
    },
    detailIndicator: {
      type: String,
      required: true,
    },
    currentDate: {
      type: Array,
      required: true,
    },
    referencyDate: {
      type: Array,
      required: true,
    },
    units: {
      type: Array,
      default: () => [],
    },
    unitsConfiguration: {
      type: Array,
      default: () => [],
    },
    segments: {
      type: Array,
      default: () => [],
    },
    segmentsObject: {
      type: Array,
      default: () => [],
    },
    products: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    loading: false,
    clientsQuantity: 0,
    selectedDetail: '',
    detailsData: {},
  }),
  computed: {
    daysAhead() {
      const today = DateTime.now()
      const sevenDaysAhead = today.plus({ days: +7 }).toFormat('yyyy-MM-dd')
      const formatToday = today.toFormat('yyyy-MM-dd')

      return [formatToday, sevenDaysAhead]
    },
    quickActionTotalClientsDecreasedAverageItemsInBasket() {
      const formatCurrentDate = this.currentDate.map((date) => date.replaceAll('-', ''))
      const formatReferencyDate = this.referencyDate.map((date) => date.replaceAll('-', ''))

      return criteriaGroup([
        totalClientsDecreasedAverageItemsInBasket(this.unitsConfiguration, this.products, formatReferencyDate, formatCurrentDate),
        ...this.segmentsObject.map((segment) => isInSegmentCriteria(segment)),
      ])
    },
    quickActionTotalClientsBelowAverageProductValue() {
      const [startDate, endDate] = this.currentDate.map((date) => date.replaceAll('-', ''))
      let averageItems = this.averageItems
      return criteriaGroup([
        totalClientsBelowAverageProductValue(this.unitsConfiguration, this.products, averageItems, startDate, endDate),
        ...this.segmentsObject.map((segment) => isInSegmentCriteria(segment)),
      ])
    },
    quickActionsCustomerNeverBought() {
      const formatCurrentDate = this.currentDate.map((date) => date.replaceAll('-', ''))

      return criteriaGroup([
        ...customerNeverBought(this.unitsConfiguration, this.products, formatCurrentDate),
        ...this.segmentsObject.map((segment) => isInSegmentCriteria(segment)),
      ])
    },
    quickActionsCustomerDidNotBoughtInPeriod() {
      const formatCurrentDate = this.currentDate.map((date) => date.replaceAll('-', ''))

      return criteriaGroup([
        ...customerDidNotBoughtInPeriod(this.unitsConfiguration, this.products, formatCurrentDate),
        ...this.segmentsObject.map((segment) => isInSegmentCriteria(segment)),
      ])
    },
    quickActionsTotalClientsDecreasedAverageProductValue() {
      const formatCurrentDate = this.currentDate.map((date) => date.replaceAll('-', ''))
      const averageProducts = this.averageProducts

      return criteriaGroup([
        totalClientsDecreasedAverageProductValue(this.unitsConfiguration, this.products, formatCurrentDate, averageProducts),
        ...this.segmentsObject.map((segment) => isInSegmentCriteria(segment)),
      ])
    },
    quickActionsTotalClientsBelowAverageCoupons() {
      const formatCurrentDate = this.currentDate.map((date) => date.replaceAll('-', ''))
      const averageCoupons = this.averageCoupons

      return criteriaGroup([
        totalClientsBelowAverageCoupons(this.unitsConfiguration, this.products, formatCurrentDate, averageCoupons),
        ...this.segmentsObject.map((segment) => isInSegmentCriteria(segment)),
      ])
    },
    quickActionsData() {
      return {
        AVERAGE_ITEMS_IN_BASKET: {
          cardOne: {
            title: 'Clientes que estão abaixo da média',
            description: 'Estes clientes estão com a média de itens na cesta inferior à média apresentado no período atual analisado.',
            quickActionGroupId: 'clients-below-average-items',
            value: this.detailsData.totalClientsBelowAverageItemsInBasket || 0,
            segmentationGroup: this.quickActionTotalClientsBelowAverageProductValue,
          },
          cardTwo: {
            title: 'Clientes que reduziram a média de itens na cesta',
            description: 'Clientes que levavam mais itens na cesta no período de referência, mas levam menos no período atual.',
            quickActionGroupId: 'clients-decreased-items',
            value: this.detailsData.totalClientsDecreasedAverageItemsInBasket || 0,
            segmentationGroup: this.quickActionTotalClientsDecreasedAverageItemsInBasket,
          },
        },
        AVERAGE_PRODUCT_VALUE: {
          cardOne: {
            title: 'Valor médio dos produtos inferior à média atual',
            description: 'Clientes que estão com o valor médio dos produtos inferior à média no período atual analisado.',
            quickActionGroupId: 'clients-below-average-product',
            value: this.detailsData.totalClientsBelowAverageProductValue || 0,
            segmentationGroup: this.quickActionsTotalClientsDecreasedAverageProductValue,
          },
        },
        UNIQUE_CUSTOMERS: {
          cardOne: {
            title: 'Não comprou no período atual',
            description: 'Clientes que já compraram alguma vez mas não compraram no período atual.',
            quickActionGroupId: 'customer-not-bought',
            value: this.detailsData.totalClientsNonBuy?.customer_did_not_bought_in_period || 0,
            segmentationGroup: this.quickActionsCustomerDidNotBoughtInPeriod,
          },
          cardTwo: {
            title: 'Cadastrou e não comprou',
            description: 'Clientes que se cadastraram no período atual mas não compraram no período atual.',
            quickActionGroupId: 'customer-never-bought',
            value: this.detailsData.totalClientsNeverBought?.customer_never_bought || 0,
            segmentationGroup: this.quickActionsCustomerNeverBought,
          },
        },
        AVERAGE_CUSTOMER_SPEND: {
          cardOne: {
            title: 'Clientes abaixo da média',
            description: 'Clientes que estão com a média de compras inferior à média do período atual analisado.',
            quickActionGroupId: 'clients-below-average-coupons',
            value: this.detailsData.totalClientsBelowAverageCoupons || 0,
            segmentationGroup: this.quickActionsTotalClientsBelowAverageCoupons,
          },
        },
      }
    },
  },
  watch: {
    detailIndicator() {
      if (this.newSearch && this.detailIndicator !== this.selectedDetail) {
        this.waitLoadDetails()
      }
      this.selectedDetail = this.detailIndicator
    },
  },
  mounted() {
    this.waitLoadDetails()
  },
  methods: {
    async waitLoadDetails() {
      this.loading = true
      try {
        this.detailsData = await this.getDetails()
      } catch (error) {
        this.$snackbar({ message: `Erro ao buscar os dados.` })
      }
      this.loading = false
    },
    getDetails() {
      return this.$apollo
        .query({
          query: QUERY_GET_DETAILS,
          variables: {
            initialDate: this.currentDate[0],
            finalDate: this.currentDate[1],
            referenceInitialDate: this.referencyDate[0],
            referenceFinalDate: this.referencyDate[1],
            units: this.units,
            units_configuration: this.unitsConfiguration,
            products: this.products,
            segments: this.segments,
          },
          fetchPolicy: 'no-cache',
        })
        .then(({ data }) => data)
    },
  },
}
</script>
